import { css } from 'twin.macro';
import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Map, Marker, GoogleApiWrapper, InfoWindow } from 'google-maps-react';
import { LoadingContainer } from '../../_elements/LoadingContainer';
import { getInitials, getMapCenterAndZoom, getRandomColor } from '../../_helpers';
import MapPointer from '../../assets/icons/map_pointer.png';
import HealthCarePointer from '../../assets/icons/healtcare.jpg';
import RealEstatePointer from '../../assets/icons/realState.png';
// import ShopPointerPointer from '../../assets/icons/shop.png';
import HighlightPointer from '../../assets/icons/pointer.gif';
import TransitOverlay from './components/TransitOverlay';
import EventRoutesOverlay from './components/EventRoutesOverlay';
import WeatherOverlay from './components/WeatherOverlay';
import { resolvePin, PIN_SIZE } from './pinResolver';
import EventBadgeOverlay from './components/EventBadgeOverlay';
import MapClusterer from './components/MapClusterer';

const mapStyles = {
    position: 'absolute',
    width: '100%',
    height: 'auto',
};

const MARKER = {
    shop: HighlightPointer,
    default: MapPointer,
    'real state': RealEstatePointer,
    'health care': HealthCarePointer,
};
function GoogleMap({
    google,
    lat,
    lng,
    showPublicChannels = true,
    setChannelLatLong,
    isChannelCreateSelected,
    setShowCreateChannelModal,
    sholudUpdatePublicChannels,
    handleChatroomsEvents,
    currentMarker,
    searchedRoomId,
    setIsEvents,
    isEvents = false,
    chatroomEvents = null,
    publicChannels,
    handleCloseActiveEvent,
    handleHideEventsModal,
    upcomingEvents = [],
    showTraffic,
    showTransit,
    showEventRoutes,
    toggleEventRoutes,
    searchActive = false,
    showWeather = false,
    ...rest
}) {
    const [center, setCenter] = useState({});
    useEffect(() => {
        setCenter({ lat: lat, lng: lng });
    }, [lat, lng]);
    const [activeMarker, setActiveMarker] = useState({});
    const [selectedPlace, setSelectedPlace] = useState({});
    const [showingInfoWindow, setShowingInfoWindow] = useState(false);
    const [chatroomId, setChatroomId] = useState('');
    const [tempChannels, setTempChannels] = useState([]);
    const [nativeMarkers, setNativeMarkers] = useState([]);
    const [isMapReady, setIsMapReady] = useState(false);

    const mapRef = useRef(null);
    const searchRefs = useRef([]);
    const nativeMarkersRef = useRef([]);
    const nativeMarkerMap = useRef({}); // channelId -> { marker, props }

    // Collect native Google Maps marker instances for clustering + click handling.
    // Native markers ALWAYS start with map: null — the clusterer manages their visibility.
    // When searching, React markers take over display; native markers stay hidden.
    useEffect(() => {
        if (!google || !isMapReady || !mapRef?.current?.map || !tempChannels?.length) {
            setNativeMarkers([]);
            return;
        }

        // Clear old markers
        nativeMarkersRef.current.forEach((m) => m.setMap(null));
        nativeMarkersRef.current = [];
        nativeMarkerMap.current = {};

        // Build native markers for each channel with an address
        tempChannels.forEach((channelData) => {
            if (!channelData?.addresses?.length) return;
            // Deduplicate addresses by lat/lng (prevents stacked flickering pins)
            const seen = new Set();
            channelData.addresses.forEach((addr) => {
                const latitude = Number(addr?.latitude);
                const longitude = Number(addr?.longitude);
                if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
                const key = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
                if (seen.has(key)) return;
                seen.add(key);

                const pin = resolvePin(channelData, MARKER);
                const marker = new google.maps.Marker({
                    position: { lat: latitude, lng: longitude },
                    icon: {
                        url: pin.url,
                        scaledSize: new google.maps.Size(PIN_SIZE.width, PIN_SIZE.height),
                    },
                    map: null, // clusterer manages visibility
                });

                // Click handler — replicates onMarkerClick for native markers
                const props = {
                    id: channelData.id || channelData._id,
                    title: channelData.title,
                    name: channelData.subTitle,
                    position: { lat: latitude, lng: longitude },
                    teamData: channelData.teamData,
                    activeEvents: channelData.activeEvents,
                };
                marker.addListener('click', () => {
                    window.setTimeout(() => {
                        setActiveMarker(marker);
                        setSelectedPlace(props);
                        setChatroomId(props?.id);
                        setShowingInfoWindow(true);
                        setCenter(props?.position);
                        setIsEvents(true);
                        handleCloseActiveEvent();
                    }, 100);
                });

                nativeMarkersRef.current.push(marker);
                nativeMarkerMap.current[channelData.id || channelData._id] = { marker, props };
            });
        });
        setNativeMarkers(nativeMarkersRef.current);

        return () => {
            nativeMarkersRef.current.forEach((m) => m.setMap(null));
            nativeMarkersRef.current = [];
            nativeMarkerMap.current = {};
            setNativeMarkers([]);
        };
    }, [google, isMapReady, tempChannels]); // NOT searchActive — markers stay map:null regardless

    //this getEventsByCategoryChannels function checks if there is chatroomEvents then it sets the public channels to the filtered event eliminating rest of the public channels and only one public channel is seted and seen

    const getEventsByCategoryChannels = async () => {
        // When searching, bypass event filter — show all results.
        if (searchActive) {
            setTempChannels(publicChannels);
            return;
        }
        if (isEvents && chatroomEvents && chatroomEvents?.length > 0) {
            const filteredArray = publicChannels?.filter((chatroom) =>
                chatroomEvents?.some((event) => chatroom._id === event?.assignedChannel[0])
            );
            setTempChannels(filteredArray);
        } else {
            setTempChannels(publicChannels);
        }
    };

    useEffect(() => {
        getEventsByCategoryChannels();
    }, [chatroomEvents, publicChannels, searchActive]);

    useEffect(() => {
        const getSelectedChatroomEvents = async () => {
            if (chatroomId) {
                if (/^[0-9a-fA-F]{24}$/.test(chatroomId)) {
                    handleChatroomsEvents(chatroomId);
                }
            }
        };
        getSelectedChatroomEvents();
    }, [chatroomId]);

    useEffect(() => {
        searchRefs.current = searchRefs.current.slice(0, publicChannels?.length);
        setTempChannels(publicChannels);
    }, [publicChannels]);

    const onMarkerClick = (props, marker, e) => {
        window.setTimeout(() => {
            setActiveMarker(marker);
            setSelectedPlace(props);
            setChatroomId(props?.id);
            setShowingInfoWindow(true);
            setCenter(props?.position);
            setIsEvents(true);
            handleCloseActiveEvent();
        }, 100);
    };

    const onInfoWindowClose = () => {
        setActiveMarker({});
        setShowingInfoWindow(false);
        handleHideEventsModal();
        setChatroomId(null);
        setSelectedPlace({});
        if (isEvents) {
            handleChatroomsEvents(null, chatroomEvents[0]?.eventType?.id);
        } else {
            setIsEvents(false);
        }
    };

    const onMapClicked = async (mapProps, map, clickEvent) => {
        if (showingInfoWindow) {
            onInfoWindowClose(map);
        }
        if (isChannelCreateSelected) {
            let latitude = clickEvent.latLng.lat();
            let longitude = clickEvent.latLng.lng();
            setChannelLatLong({
                latitude: latitude,
                longitude: longitude,
            });
            setShowCreateChannelModal(true);
        }
    };

    useEffect(() => {
        if (!searchedRoomId) return;
        // Try native marker map first (works when not searching)
        const entry = nativeMarkerMap.current[searchedRoomId];
        if (entry) {
            const { marker, props } = entry;
            window.setTimeout(() => {
                setActiveMarker(marker);
                setSelectedPlace(props);
                setChatroomId(props?.id);
                setShowingInfoWindow(true);
                setCenter(props?.position);
                setIsEvents(true);
                handleCloseActiveEvent();
            }, 100);
            return;
        }
        // Fallback: React markers (when searching)
        if (searchRefs.current.length > 0) {
            const searchedMarker = searchRefs.current.find((markerRef) => markerRef?.props?.id === searchedRoomId);
            if (searchedMarker) {
                onMarkerClick(searchedMarker.props, searchedMarker.marker);
            }
        }
    }, [searchedRoomId, tempChannels]);

    useEffect(() => {
        if (isEvents && chatroomEvents?.length > 0 && tempChannels?.length > 0 && mapRef.current) {
            const locations = tempChannels
                .map((channelData) =>
                    channelData?.addresses
                        .map((channel) => ({ lat: Number(channel.latitude), lng: Number(channel.longitude) }))
                        .filter((location) => Number.isFinite(location.lat) && Number.isFinite(location.lng))
                )
                .flat();

            const { center: newCenter, zoom } = getMapCenterAndZoom(google, locations);
            setCenter(newCenter);
            mapRef.current.map.setZoom(zoom);
        }
    }, [chatroomEvents?.length, isEvents, google?.maps?.LatLng, google?.maps?.LatLngBounds, tempChannels]);

    useEffect(() => {
        if (!google || !mapRef || !mapRef?.current) return;
        let trafficLayer = null;
        if (showTraffic) {
            trafficLayer = new google.maps.TrafficLayer();
            trafficLayer.setMap(mapRef.current.map);
        }
        return () => {
            if (trafficLayer) {
                trafficLayer.setMap(null);
            }
        };
    }, [google, showTraffic, (mapRef || {}).current]);
    return (
        <Fragment>
            {google && (
                <Map
                    google={google}
                    onClick={onMapClicked}
                    style={mapStyles}
                    ref={mapRef}
                    onReady={() => setIsMapReady(true)}
                    zoom={15}
                    center={{ lat: center?.lat, lng: center?.lng }}
                    streetViewControlOptions={{
                        position: google.maps.ControlPosition.RIGHT_TOP,
                    }}
                    zoomControlOptions={{
                        position: google.maps.ControlPosition.RIGHT_TOP,
                    }}
                >
                    {showWeather && <WeatherOverlay lat={center?.lat} lng={center?.lng} show={true} />}
                    {showTransit && google && (mapRef?.current || {})?.map && (
                        <TransitOverlay
                            visible={true}
                            google={google}
                            mapRef={mapRef}
                            center={center}
                            map={mapRef.current.map}
                        />
                    )}
                    {showEventRoutes && (
                        <EventRoutesOverlay
                            visible={showEventRoutes}
                            google={google}
                            currentPosition={{ lat, lng }}
                            events={upcomingEvents}
                            onToggle={toggleEventRoutes}
                            mapRef={mapRef}
                            map={mapRef?.current?.map}
                        />
                    )}

                    {currentMarker ? (
                        <Marker
                            {...rest}
                            name={'current location'}
                            icon={{
                                url: currentMarker,
                                scaledSize: new google.maps.Size(PIN_SIZE.width, PIN_SIZE.height),
                            }}
                        />
                    ) : (
                        <Marker
                            {...rest}
                            name={'current location'}
                            position={{
                                lat: lat,
                                lng: lng,
                            }}
                        />
                    )}

                    {searchActive &&
                        showPublicChannels &&
                        tempChannels?.map((channelData, channelIndex) => {
                            return channelData?.addresses[0]
                                ? channelData?.addresses.map((channel, index) => {
                                      const pin = resolvePin(channelData, MARKER);
                                      return (
                                          <Marker
                                              key={`${channelData.id || channelData._id}_${index}`}
                                              title={channelData.title}
                                              name={channelData.subTitle}
                                              id={channelData.id || channelData._id}
                                              icon={{
                                                  url: pin.url,
                                                  scaledSize: new google.maps.Size(PIN_SIZE.width, PIN_SIZE.height),
                                              }}
                                              onClick={onMarkerClick}
                                              position={{
                                                  lat: channel?.latitude,
                                                  lng: channel?.longitude,
                                              }}
                                              teamData={channelData.teamData}
                                              activeEvents={channelData.activeEvents}
                                              hasEventBadge={pin.hasEventBadge}
                                              eventIconUrl={pin.eventIconUrl}
                                              className="mapp"
                                              ref={(el) => (searchRefs.current[channelIndex] = el)}
                                          />
                                      );
                                  })
                                : null;
                        })}
                    {/* Event badge fade animation — only when searching (individual pins visible) */}
                    {searchActive &&
                        showPublicChannels &&
                        google &&
                        mapRef?.current?.map &&
                        tempChannels
                            ?.filter((ch) => ch?.activeEvents?.length > 0 && ch?.addresses[0])
                            ?.map((channelData) => {
                                const pin = resolvePin(channelData, MARKER);
                                return channelData.addresses.map((channel, idx) => (
                                    <EventBadgeOverlay
                                        key={`badge-${channelData.id}-${idx}`}
                                        google={google}
                                        map={mapRef.current.map}
                                        position={{ lat: channel.latitude, lng: channel.longitude }}
                                        defaultPinUrl={pin.url}
                                        eventIconUrl={pin.eventIconUrl}
                                        pinSize={PIN_SIZE}
                                    />
                                ));
                            })}
                    {/* Zoom-aware clustering (disabled during Bob search for exact results) */}
                    {google && mapRef?.current?.map && (
                        <MapClusterer
                            google={google}
                            map={mapRef.current.map}
                            markers={nativeMarkers}
                            searchActive={searchActive}
                            pinSize={PIN_SIZE}
                        />
                    )}
                    <InfoWindow
                        marker={activeMarker}
                        className="bg-dark"
                        onClose={onInfoWindowClose}
                        visible={showingInfoWindow && isEvents}
                    >
                        <div
                            css={css`
                                width: 300px;
                                padding: 0.5em;
                            `}
                        >
                            <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    justify-content: space-between;
                                    align-items: center;
                                `}
                            >
                                <div
                                    css={css`
                                        overflow: hidden;
                                        width: 100px;
                                        height: 100px;
                                        border-radius: 1000px;
                                        border: 2px solid #e3e3e3;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        color: white;
                                        background-color: ${selectedPlace?.teamData?.avatar
                                            ? 'none'
                                            : getRandomColor(selectedPlace?.teamData?._id)};
                                        font-weight: 400;
                                        font-size: 2.3rem;
                                        margin-bottom: 0.5rem;
                                    `}
                                >
                                    {selectedPlace?.teamData?.avatar ? (
                                        <img
                                            css={css`
                                                object-fit: contain;
                                                max-width: 100% !important;
                                            `}
                                            src={selectedPlace?.teamData?.avatar}
                                            alt={selectedPlace?.teamData?.name}
                                        />
                                    ) : (
                                        getInitials(selectedPlace?.teamData?.name)
                                    )}
                                </div>
                                <h3
                                    className="text-dark"
                                    css={css`
                                        overflow: hidden;
                                        position: relative;
                                        word-break: break-all;
                                        flex: 1;
                                        max-height: 3.6em; /* exactly three lines */
                                        display: -webkit-box;
                                        -webkit-line-clamp: 3;
                                        -webkit-box-orient: vertical;
                                        margin-bottom: 0.5rem;
                                    `}
                                >
                                    {selectedPlace?.teamData?.name}
                                </h3>
                                <div
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                    `}
                                >
                                    <h4
                                        className="text-dark mb-0"
                                        css={css`
                                            text-overflow: ellipsis;
                                            overflow: hidden;
                                            overflow-wrap: break-word;
                                            width: 200px;
                                            max-height: 3.6em;
                                            display: -webkit-box;
                                            -webkit-line-clamp: 3;
                                            -webkit-box-orient: vertical;
                                            font-style: italic;
                                            text-align: center;
                                        `}
                                    >
                                        <a href={`teams/${selectedPlace.id}`} rel="noreferrer">
                                            {selectedPlace.title}
                                        </a>
                                    </h4>
                                </div>
                                <p
                                    className="text-dark mb-0"
                                    css={css`
                                        overflow: hidden;
                                        max-height: calc(5 * 1.2em);
                                        line-height: 1.2em;
                                        display: -webkit-box;
                                        -webkit-line-clamp: 3;
                                        -webkit-box-orient: vertical;
                                        width: 100%;
                                        text-align: center;
                                        text-overflow: ellipsis;
                                    `}
                                >
                                    {selectedPlace.name}
                                </p>
                                <p className="text-dark mb-0 mt-2">
                                    Go to&nbsp;
                                    <b>
                                        <a href={`teams/${selectedPlace.id}`} rel="noreferrer">
                                            Channel
                                        </a>
                                    </b>
                                </p>
                            </div>
                        </div>
                    </InfoWindow>
                </Map>
            )}
        </Fragment>
    );
}

export default GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    LoadingContainer: LoadingContainer,
})(GoogleMap);
